/**
* Modern Admin Actions
* 
* This file provides action functions that use the modern service layer.
* It maintains backward compatibility with existing components while 
* leveraging the new API architecture.
*/

import {
    userService,
    rbacService,
    patientService,
    dynamicFormsService,
    masterDataService,
    medicationService,
    schedulingService,
    documentService,
    dashboardService,
    auditService
} from '../services';

// ============================================================================
// USER MANAGEMENT ACTIONS
// ============================================================================

export const getUserListAction = async (params = {}) => {
    try {
        return await userService.getUsers(params);
    } catch (error) {
        console.error('Error in getUserListAction:', error);
        throw error;
    }
};

export const getStaffListAction = async (params = {}) => {
    try {
        // Transform legacy parameters to modern format
        const modernParams = {
            page: params.page || 1,
            page_size: params.page_size || params.per_page || 50,
            search: params.search || params.name,
            role: params.role,
            department: params.department,
            is_active: params.is_active !== undefined ? params.is_active :
                params.status === 'active' ? true :
                    params.status === 'inactive' ? false : null
        };

        return await userService.getUsers(modernParams);
    } catch (error) {
        console.error('Error in getStaffListAction:', error);
        throw error;
    }
};

export const addStaffAction = async (staffData) => {
    try {
        console.log('addStaffAction - Input:', staffData);

        const response = await userService.createUser(staffData);

        console.log('addStaffAction - Response:', response);

        // Ensure consistent response format
        return {
            success: response.success !== false,
            msg: response.message || response.msg || "Staff member added successfully",
            message: response.message || response.msg || "Staff member added successfully",
            data: response.data
        };
    } catch (error) {
        console.error('Error in addStaffAction:', error);
        return {
            success: false,
            msg: error.message || "Failed to add staff member",
            message: error.message || "Failed to add staff member"
        };
    }
};

export const updateStaffAction = async (staffId, staffData, originalData = null) => {
    try {
        return await userService.updateStaffProfile(staffId, staffData, originalData);
    } catch (error) {
        console.error('Error in updateStaffAction:', error);
        throw error;
    }
};

export const updateStaffStatusAction = async (statusData) => {
    try {
        // Transform legacy format to modern format
        const updateData = {
            is_active: statusData.status === 1
        };

        console.log('updateStaffStatusAction - Input:', statusData);
        console.log('updateStaffStatusAction - Transformed:', updateData);

        const response = await userService.updateUser(statusData.id, updateData);

        console.log('updateStaffStatusAction - Response:', response);

        // Ensure consistent response format
        return {
            success: response.success !== false,
            msg: response.message || response.msg || "Staff status updated successfully",
            message: response.message || response.msg || "Staff status updated successfully",
            data: response.data
        };
    } catch (error) {
        console.error('Error in updateStaffStatusAction:', error);
        return {
            success: false,
            msg: error.message || "Failed to update staff status",
            message: error.message || "Failed to update staff status"
        };
    }
};

export const getStaffDetailsAction = async (staffId) => {
    try {
        return await userService.getUserById(staffId);
    } catch (error) {
        console.error('Error in getStaffDetailsAction:', error);
        throw error;
    }
};

export const changePasswordAction = async (passwordData) => {
    try {
        return await userService.changePassword(passwordData);
    } catch (error) {
        console.error('Error in changePasswordAction:', error);
        throw error;
    }
};

export const updateFcmTokenAction = async (tokenData) => {
    try {
        return await userService.updateCurrentUserProfile(tokenData);
    } catch (error) {
        console.error('Error in updateFcmTokenAction:', error);
        throw error;
    }
};

// ============================================================================
// RBAC MANAGEMENT ACTIONS
// ============================================================================

export const getUserGroupListAction = async (params = {}) => {
    try {
        const result = await rbacService.getRoles(params);
        return result;
    } catch (error) {
        console.error('Error in getUserGroupListAction:', error);
        return {
            success: false,
            data: [],
            total: 0,
            message: error.message || "Failed to fetch user groups"
        };
    }
};

export const addUserGroupAction = async (groupData) => {
    try {
        return await rbacService.createRole(groupData);
    } catch (error) {
        console.error('Error in addUserGroupAction:', error);
        throw error;
    }
};

export const updateUserGroupAction = async (groupId, groupData) => {
    try {
        return await rbacService.updateRole(groupId, groupData);
    } catch (error) {
        console.error('Error in updateUserGroupAction:', error);
        throw error;
    }
};

export const getGroupDetailsAction = async (groupId) => {
    try {
        const result = await rbacService.getRoleById(groupId);
        // Ensure we return the correct format for the frontend
        if (result) {
            return {
                success: true,
                data: {
                    name: result.name || result.display_name,
                    description: result.description,
                    is_active: result.is_active,
                    role_id: result.role_id,
                    ...result
                }
            };
        } else {
            return {
                success: false,
                message: "Role not found"
            };
        }
    } catch (error) {
        console.error('Error in getGroupDetailsAction:', error);
        return {
            success: false,
            message: error.message || "Failed to load user group details"
        };
    }
};

export const getGroupTypesAction = async (params = {}) => {
    try {
        return await rbacService.getRoles(params);
    } catch (error) {
        console.error('Error in getGroupTypesAction:', error);
        throw error;
    }
};

export const getAllRolesApi = async () => {
    try {
        return await rbacService.getRoles();
    } catch (error) {
        console.error('Error in getAllRolesApi:', error);
        throw error;
    }
};

export const getFormListWithPermissionAction = async (params) => {
    try {
        return await rbacService.getFormListWithPermissions(params);
    } catch (error) {
        console.error('Error in getFormListWithPermissionAction:', error);
        return {
            success: false,
            data: [],
            message: "Failed to fetch form permissions"
        };
    }
};

export const updateFormsPermissionAction = async (permissionData) => {
    try {
        const { userId, formsPermission } = permissionData;

        if (userId) {
            // Update user permissions directly
            return await rbacService.updateUserPermissions(permissionData);
        } else if (permissionData.roleId) {
            // Update role permissions
            return await rbacService.assignPermissionsToRole(permissionData.roleId, formsPermission);
        } else {
            throw new Error("Either userId or roleId must be provided");
        }
    } catch (error) {
        console.error('Error in updateFormsPermissionAction:', error);
        return {
            success: false,
            message: error.message || "Failed to update permissions"
        };
    }
};

export const getGroupListWithPermissionAction = async (params) => {
    try {
        return await rbacService.getRoles(params);
    } catch (error) {
        console.error('Error in getGroupListWithPermissionAction:', error);
        throw error;
    }
};

export const updateGroupsPermissionAction = async (permissionData) => {
    try {
        return await rbacService.assignPermissionsToRole(permissionData.roleId, permissionData.permissions);
    } catch (error) {
        console.error('Error in updateGroupsPermissionAction:', error);
        throw error;
    }
};

export const getUserComprehensivePermissionsAction = async (userId) => {
    try {
        return await rbacService.getUserComprehensivePermissions(userId);
    } catch (error) {
        console.error('Error in getUserComprehensivePermissionsAction:', error);
        return {
            success: false,
            data: {},
            message: "Failed to fetch user permissions"
        };
    }
};

export const updateUserFormPermissionsAction = async (userId, formPermissions) => {
    try {
        return await rbacService.updateUserFormPermissions(userId, formPermissions);
    } catch (error) {
        console.error('Error in updateUserFormPermissionsAction:', error);
        return {
            success: false,
            message: "Failed to update form permissions"
        };
    }
};

// ============================================================================
// PATIENT MANAGEMENT ACTIONS
// ============================================================================

export const getPatientListAction = async (params = {}) => {
    try {
        return await patientService.getPatients(params);
    } catch (error) {
        console.error('Error in getPatientListAction:', error);
        throw error;
    }
};

export const addPatientAction = async (patientData) => {
    console.log('🎯 ADD PATIENT ACTION CALLED');
    console.log('📥 Input patient data:', patientData);
    
    try {
        // Data is already in modern format, no transformation needed
        console.log('=== ADD PATIENT ACTION: INPUT DATA ===', patientData);
        console.log('🌐 Calling patientService.createPatientPreRegistration...');
        
        const result = await patientService.createPatientPreRegistration(patientData);
        
        console.log('✅ patientService.createPatientPreRegistration completed successfully');
        console.log('📤 Result from service:', result);
        
        return result;
    } catch (error) {
        console.error('💥 ERROR in addPatientAction:', error.message);
        
        // Re-throw the error to be handled by the calling function
        throw error;
    }
};

export const updatePatientAction = async (patientId, patientData) => {
    try {
        return await patientService.updatePatient(patientId, patientData);
    } catch (error) {
        console.error('Error in updatePatientAction:', error);
        throw error;
    }
};

export const completePatientRegistrationAction = async (registrationData) => {
    try {
        console.log('Completing patient registration with data:', registrationData);
        return await patientService.completePatientRegistration(registrationData);
    } catch (error) {
        console.error('Error in completePatientRegistrationAction:', error);
        throw error;
    }
};

export const updatePatientCredentialsAction = async (patientId, patientData) => {
    try {
        return await patientService.updatePatient(patientId, patientData);
    } catch (error) {
        console.error('Error in updatePatientCredentialsAction:', error);
        throw error;
    }
};

export const getPatientDetailsAction = async (patientId) => {
    try {
        return await patientService.getPatientById(patientId);
    } catch (error) {
        console.error('Error in getPatientDetailsAction:', error);
        throw error;
    }
};

export const getpatientDetailsbyIdAction = async (patientId) => {
    try {
        return await patientService.getPatientById(patientId);
    } catch (error) {
        console.error('Error in getpatientDetailsbyIdAction:', error);
        throw error;
    }
};

export const getAllPatientsCredentialsAction = async (params = {}) => {
    try {
        return await patientService.getPatients(params);
    } catch (error) {
        console.error('Error in getAllPatientsCredentialsAction:', error);
        throw error;
    }
};

export const getPatientDataAction = async (params) => {
    try {
        return await patientService.searchPatients(params);
    } catch (error) {
        console.error('Error in getPatientDataAction:', error);
        throw error;
    }
};

export const getPatientDataByMRNAction = async (mrn) => {
    try {
        return await patientService.searchPatientByMRN(mrn);
    } catch (error) {
        console.error('Error in getPatientDataByMRNAction:', error);
        throw error;
    }
};

export const hendleSearchPatientAction = async (searchParams) => {
    try {
        return await patientService.searchPatients(searchParams);
    } catch (error) {
        console.error('Error in hendleSearchPatientAction:', error);
        throw error;
    }
};

export const getpatientDetailsById = async (patientId) => {
    try {
        return await patientService.getPatientById(patientId);
    } catch (error) {
        console.error('Error in getpatientDetailsById:', error);
        throw error;
    }
};

export const patientStatusUpdateByIdAction = async (patientId, statusData) => {
    try {
        const data = { is_active: statusData.status === "1" };
        return await patientService.updatePatient(patientId, data);
    } catch (error) {
        console.error('Error in patientStatusUpdateByIdAction:', error);
        throw error;
    }
};

export const getPatiantUniqueIDAndMrnNumByIDAction = async (patientId) => {
    try {
        // Assuming getPatientById returns the necessary ID and MRN
        return await patientService.getPatientById(patientId);
    } catch (error) {
        console.error('Error in getPatiantUniqueIDAndMrnNumByIDAction:', error);
        throw error;
    }
};

export const getpatientFormDetailsById = async (patientId) => {
    try {
        return await dynamicFormsService.getFormSubmissions(null, { patient_id: patientId });
    } catch (error) {
        console.error('Error in getpatientFormDetailsById:', error);
        throw error;
    }
};

export const getPatiendInfoAction = async (patientId) => {
    try {
        return await patientService.getPatientById(patientId);
    } catch (error) {
        console.error('Error in getPatiendInfoAction:', error);
        throw error;
    }
};

export const getFormListAction = async (params = {}) => {
    try {
        console.log('🎯 getFormListAction called with params:', params);
        console.log('🌐 About to call dynamicFormsService.getForms...');

        const result = await dynamicFormsService.getForms(params);

        console.log('📤 getFormListAction result:', {
            success: result?.success,
            dataLength: result?.data?.length,
            total: result?.total,
            result: result
        });

        return result;
    } catch (error) {
        console.error('💥 Error in getFormListAction:', error);
        throw error;
    }
};

export const saveWebFormAction = async (data) => {
    try {
        const response = await dynamicFormsService.createForm(data);
        return response;
    } catch (error) {
        console.error('Error in saveWebFormAction:', error);
        throw error;
    }
};

export const updateWebFormAction = async (formId, data, version = null) => {
    // console.log("modal data", data)
    try {
        return await dynamicFormsService.updateForm(formId, data, version);
    } catch (error) {
        console.error('Error in updateWebFormAction:', error);
        throw error;
    }
};

export const deleteWebFormAction = async (formId) => {
    try {
        return await dynamicFormsService.deleteForm(formId.id);
    } catch (error) {
        console.error('Error in deleteWebFormAction:', error);
        throw error;
    }
};

export const getFormDetailsAction = async (form_id, version = null) => {
    try {
        return await dynamicFormsService.getFormById(form_id, version);
    } catch (error) {
        console.error('Error in getFormDetailsAction:', error);
        throw error;
    }
};

export const getFormFieldsDataAction = async (formId, version = null) => {
    try {
        // This endpoint doesn't exist in the modern service.
        // Assuming we need to fetch form details which include fields.
        const response = await dynamicFormsService.getFormById(formId, version);
        if (response.success) {
            return {
                ...response,
                data: response.data?.structure?.fields || []
            };
        }
        return response;
    } catch (error) {
        console.error('Error in getFormFieldsDataAction:', error);
        throw error;
    }
};

export const addWebFormFieldsAction = async (formId, fieldData, version = null) => {
    try {
        console.log('addWebFormFieldsAction - formId:', formId);
        console.log('addWebFormFieldsAction - fieldData:', fieldData);
        
        // This requires updating the form structure.
        const formResponse = await dynamicFormsService.getFormById(formId, version);
        if (!formResponse.success) {
            throw new Error("Form not found");
        }
        const existingForm = formResponse.data;
        console.log('addWebFormFieldsAction - existingForm:', existingForm);
        
        const updatedFields = [...(existingForm.fields || []), ...(fieldData.fields || [])];
        console.log('addWebFormFieldsAction - updatedFields:', updatedFields);
        
        return await dynamicFormsService.updateForm(formId, { fields: updatedFields }, version);
    } catch (error) {
        console.error('Error in addWebFormFieldsAction:', error);
        console.error('Error details:', error);
        throw error;
    }
};

export const deleteWebFormFieldAction = async (formId, fieldId, version = null) => {
    try {
        // This requires updating the form structure.
        const formResponse = await dynamicFormsService.getFormById(formId, version);
        if (!formResponse.success) {
            throw new Error("Form not found");
        }
        const existingForm = formResponse.data;
        const newFields = (existingForm.fields || []).filter(f => f.field_id !== fieldId);
        return await dynamicFormsService.updateForm(formId, { fields: newFields }, version);
    } catch (error) {
        console.error('Error in deleteWebFormFieldAction:', error);
        throw error;
    }
};

export const submitWebFormActionData = async (formId, submissionData) => {
    try {
        return await dynamicFormsService.submitForm(formId, submissionData);
    } catch (error) {
        console.error('Error in submitWebFormActionData:', error);
        throw error;
    }
};

export const getFormListForStaffAction = async (params = {}) => {
    try {
        // This can be handled by getForms with appropriate RBAC on the backend
        return await dynamicFormsService.getForms(params);
    } catch (error) {
        console.error('Error in getFormListForStaffAction:', error);
        throw error;
    }
};

export const getFormListForMenuAction = async (params = {}) => {
    try {
        // This can be handled by getForms with appropriate RBAC on the backend
        return await dynamicFormsService.getForms(params);
    } catch (error) {
        console.error('Error in getFormListForMenuAction:', error);
        throw error;
    }
};

export const insertFormDataAction = async (formId, formData) => {
    try {
        return await dynamicFormsService.submitForm(formId, formData);
    } catch (error) {
        console.error('Error in insertFormDataAction:', error);
        throw error;
    }
};

export const insertFormDataActionForSubForm = async (formId, formData) => {
    try {
        // Assuming sub-form submission is the same as regular submission
        return await dynamicFormsService.submitForm(formId, formData);
    } catch (error) {
        console.error('Error in insertFormDataActionForSubForm:', error);
        throw error;
    }
};

export const getFormDataListAction = async (params = {}) => {
    try {
        const { formId, ...rest } = params;
        return await dynamicFormsService.getFormSubmissions(formId, rest);
    } catch (error) {
        console.error('Error in getFormDataListAction:', error);
        throw error;
    }
};

export const getFormDataListByStaffAction = async (params = {}) => {
    try {
        // Backend service should handle filtering by staff
        const { formId, ...rest } = params;
        return await dynamicFormsService.getFormSubmissions(formId, rest);
    } catch (error) {
        console.error('Error in getFormDataListByStaffAction:', error);
        throw error;
    }
};

export const getFormDataListDetailByIdAction = async (formId, submissionId) => {
    try {
        return await dynamicFormsService.getFormSubmission(submissionId);
    } catch (error) {
        console.error('Error in getFormDataListDetailByIdAction:', error);
        throw error;
    }
};

// ============================================================================
// DEPARTMENT & CATEGORY MANAGEMENT ACTIONS
// ============================================================================

export const getDepartmentAction = async (params = {}) => {
    try {
        // Assuming fetching a list of departments
        return await dynamicFormsService.getDepartments(params);
    } catch (error) {
        console.error('Error in getDepartmentAction:', error);
        return { success: false, data: [], message: error.message };
    }
};

export const addDepartmentAction = async (data) => {
    try {
        return await dynamicFormsService.createDepartment(data);
    } catch (error) {
        console.error('Error in addDepartmentAction:', error);
        throw error;
    }
};

export const getDepartmentListAction = async (params = {}) => {
    try {
        return await dynamicFormsService.getDepartments(params);
    } catch (error) {
        console.error('Error in getDepartmentListAction:', error);
        throw error;
    }
};

export const getDepartmentDetailsAction = async (departmentId) => {
    try {
        return await dynamicFormsService.getDepartmentById(departmentId);
    } catch (error) {
        console.error('Error in getDepartmentDetailsAction:', error);
        throw error;
    }
};

export const getCategoryListAction = async (params = {}) => {
    try {
        return await dynamicFormsService.getCategories(params);
    } catch (error) {
        console.error('Error fetching category list:', error);
        return {
            success: false,
            data: [],
            total: 0,
            message: 'Failed to fetch categories'
        };
    }
};

export const getCategoryListForWebFormAction = async (params = {}) => {
    try {
        return await dynamicFormsService.getCategories(params);
    } catch (error) {
        console.error('Error in getCategoryListForWebFormAction:', error);
        throw error;
    }
};

// ============================================================================
// FOLDER MANAGEMENT ACTIONS
// ============================================================================

export const getFoldersAction = async (params = {}) => {
    try {
        return await dynamicFormsService.getFolders(params);
    } catch (error) {
        console.error('Error in getFoldersAction:', error);
        return { success: false, data: [], message: error.message };
    }
};

export const getFolderDetailsAction = async (folderId) => {
    try {
        return await dynamicFormsService.getFolderById(folderId);
    } catch (error) {
        console.error('Error in getFolderDetailsAction:', error);
        throw error;
    }
};

export const createFolderAction = async (folderData) => {
    try {
        return await dynamicFormsService.createFolder(folderData);
    } catch (error) {
        console.error('Error in createFolderAction:', error);
        throw error;
    }
};

export const updateFolderAction = async (folderId, folderData) => {
    try {
        return await dynamicFormsService.updateFolder(folderId, folderData);
    } catch (error) {
        console.error('Error in updateFolderAction:', error);
        throw error;
    }
};

export const deleteFolderAction = async (folderId) => {
    try {
        return await dynamicFormsService.deleteFolder(folderId);
    } catch (error) {
        console.error('Error in deleteFolderAction:', error);
        throw error;
    }
};

export const getFieldTypeAction = async () => {
    try {
        const response = await dynamicFormsService.getFieldTypes();
        return {
            ...response,
            data: response.data || [] // Ensure data is an array
        };
    } catch (error) {
        console.error('Error in getFieldTypeAction:', error);
        return { success: false, data: [], message: 'Failed to fetch field types' };
    }
};

export const getFormTypeListAction = async (departmentId = null) => {
    try {
        return await dynamicFormsService.getFormTypes(departmentId);
    } catch (error) {
        console.error('Error in getFormTypeListAction:', error);
        throw error;
    }
};

export const getDepartmentMenuesAction = async (data) => {
    try {
        // This endpoint seems to be legacy and does not exist in the new backend.
        // Returning a successful but empty response to avoid breaking components.
        console.warn('getDepartmentMenuesAction is deprecated and returns no data.');
        return { success: true, data: [], message: 'Endpoint deprecated' };
    } catch (error) {
        console.error('Error in getDepartmentMenuesAction:', error);
        throw error;
    }
};

export const getDepartmentFormsAction = async (departmentId) => {
    try {
        // Filter forms by department ID
        return await dynamicFormsService.getForms({ department_id: departmentId });
    } catch (error) {
        console.error('Error in getDepartmentFormsAction:', error);
        throw error;
    }
};

export const getDepartmentFormsTypeAction = async (departmentId) => {
    try {
        // This seems to be the same as getting forms for a department
        return await dynamicFormsService.getForms({ department_id: departmentId });
    } catch (error) {
        console.error('Error in getDepartmentFormsTypeAction:', error);
        throw error;
    }
};

export const getFormCategoryAction = async (params = {}) => {
    try {
        return await dynamicFormsService.getCategories(params);
    } catch (error) {
        console.error('Error in getFormCategoryAction:', error);
        throw error;
    }
};

export const getDepartmentFormsListAction = async (departmentId) => {
    try {
        return await dynamicFormsService.getForms({ department_id: departmentId });
    } catch (error) {
        console.error('Error in getDepartmentFormsListAction:', error);
        throw error;
    }
};

export const addDepartmentFormsAction = async (departmentId, formData) => {
    try {
        const dataWithDept = { ...formData, department_id: departmentId };
        return await dynamicFormsService.createForm(dataWithDept);
    } catch (error) {
        console.error('Error in addDepartmentFormsAction:', error);
        throw error;
    }
};

export const getDepartmentFormDetailsAction = async (formId) => {
    try {
        return await dynamicFormsService.getFormById(formId);
    } catch (error) {
        console.error('Error in getDepartmentFormDetailsAction:', error);
        throw error;
    }
};

export const updateDepartmentFormAction = async (formId, formData) => {
    try {
        return await dynamicFormsService.updateForm(formId, formData);
    } catch (error) {
        console.error('Error in updateDepartmentFormAction:', error);
        throw error;
    }
};

// ============================================================================
// MASTER DATA MANAGEMENT ACTIONS
// ============================================================================

export const getMasterListAction = async (params = {}) => {
    try {
        const result = await masterDataService.getMasterData(params);
        return result;
    } catch (error) {
        console.error('Error in getMasterListAction:', error);
        return {
            success: false,
            data: [],
            total: 0,
            message: error.message || "Failed to fetch master list"
        };
    }
};

export const saveMasterDataAction = async (masterData) => {
    try {
        if (masterData.id) {
            return await masterDataService.updateMasterData(masterData.id, masterData);
        } else {
            return await masterDataService.createMasterData(masterData);
        }
    } catch (error) {
        console.error('Error in saveMasterDataAction:', error);
        throw error;
    }
};

export const deleteMasterDataAction = async (masterDataId) => {
    try {
        return await masterDataService.deleteMasterData(masterDataId);
    } catch (error) {
        console.error('Error in deleteMasterDataAction:', error);
        throw error;
    }
};

export const getCPTMasterListAction = async (params = {}) => {
    try {
        return await masterDataService.getCPTCodes(params);
    } catch (error) {
        console.error('Error in getCPTMasterListAction:', error);
        throw error;
    }
};

export const addCPTMasterAction = async (cptData) => {
    try {
        return await masterDataService.createMasterData({ ...cptData, type: 'cpt_code' });
    } catch (error) {
        console.error('Error in addCPTMasterAction:', error);
        throw error;
    }
};

export const deleteCPTMasterDataAction = async (cptId) => {
    try {
        return await masterDataService.deleteMasterData(cptId);
    } catch (error) {
        console.error('Error in deleteCPTMasterDataAction:', error);
        throw error;
    }
};

export const getDrugMasterListAction = async (params = {}) => {
    try {
        return await masterDataService.getDrugMasterData(params);
    } catch (error) {
        console.error('Error in getDrugMasterListAction:', error);
        throw error;
    }
};

export const getHCPCSMasterListAction = async (params = {}) => {
    try {
        return await masterDataService.getHCPCSCodes(params);
    } catch (error) {
        console.error('Error in getHCPCSMasterListAction:', error);
        throw error;
    }
};

export const addHPCSMasterAction = async (hcpcsData) => {
    try {
        return await masterDataService.createMasterData({ ...hcpcsData, type: 'hcpcs_code' });
    } catch (error) {
        console.error('Error in addHPCSMasterAction:', error);
        throw error;
    }
};

export const deleteHPCSMasterDataAction = async (hcpcsId) => {
    try {
        return await masterDataService.deleteMasterData(hcpcsId);
    } catch (error) {
        console.error('Error in deleteHPCSMasterDataAction:', error);
        throw error;
    }
};

export const getICDMasterListAction = async (params = {}) => {
    try {
        return await masterDataService.getICD10Codes(params);
    } catch (error) {
        console.error('Error in getICDMasterListAction:', error);
        throw error;
    }
};

export const addICDMasterAction = async (icdData) => {
    try {
        return await masterDataService.createMasterData({ ...icdData, type: 'icd10_code' });
    } catch (error) {
        console.error('Error in addICDMasterAction:', error);
        throw error;
    }
};

export const deleteICDMasterDataAction = async (icdId) => {
    try {
        return await masterDataService.deleteMasterData(icdId);
    } catch (error) {
        console.error('Error in deleteICDMasterDataAction:', error);
        throw error;
    }
};

export const getMasterTableDataAction = async (params = {}) => {
    try {
        return await masterDataService.getMasterData(params);
    } catch (error) {
        console.error('Error in getMasterTableDataAction:', error);
        throw error;
    }
};

export const getMasterDataDetailsAction = async (masterDataId) => {
    try {
        return await masterDataService.getMasterDataById(masterDataId);
    } catch (error) {
        console.error('Error in getMasterDataDetailsAction:', error);
        throw error;
    }
};

export const getMasterlistDetailByNameAction = async (name) => {
    try {
        return await masterDataService.searchMasterData({ name });
    } catch (error) {
        console.error('Error in getMasterlistDetailByNameAction:', error);
        throw error;
    }
};

export const addMasterDataDetailsAction = async (masterData) => {
    try {
        return await masterDataService.createMasterData(masterData);
    } catch (error) {
        console.error('Error in addMasterDataDetailsAction:', error);
        throw error;
    }
};

export const updateMasterTableAction = async (masterDataId, masterData) => {
    try {
        return await masterDataService.updateMasterData(masterDataId, masterData);
    } catch (error) {
        console.error('Error in updateMasterTableAction:', error);
        throw error;
    }
};

export const importExcelFileAction = async (fileData) => {
    try {
        return await masterDataService.importMasterData(fileData);
    } catch (error) {
        console.error('Error in importExcelFileAction:', error);
        throw error;
    }
};

export const getMasterTableColumnAction = async (data) => {
    try {
        // Use modern service to get master data by name
        const tableName = data.name || data.table_name;
        if (tableName) {
            const result = await masterDataService.searchMasterData({ name: tableName });
            return result;
        } else {
            const result = await masterDataService.getMasterData(data);
            return result;
        }
    } catch (error) {
        console.error('Error in getMasterTableColumnAction:', error);
        return {
            success: false,
            data: [],
            total: 0,
            message: error.message || "Failed to fetch master data tables"
        };
    }
};

// ============================================================================
// UTILITY ACTIONS
// ============================================================================

export const getPivotedFormDataAction = async (params = {}) => {
    try {
        return await dynamicFormsService.getFormSubmissions(params.formId, params);
    } catch (error) {
        console.error('Error in getPivotedFormDataAction:', error);
        throw error;
    }
};

export const updateAddendumStatusAction = async (submissionId, statusData) => {
    try {
        // This would need to be implemented in the forms service
        return { success: true, message: "Addendum status updated successfully" };
    } catch (error) {
        console.error('Error in updateAddendumStatusAction:', error);
        throw error;
    }
};

// ============================================================================
// MASTER DATA INTEGRATION ACTIONS FOR DYNAMIC FORMS
// ============================================================================

/**
 * Get column options from master data table
 * @param {string} tableId - Master data table ID
 * @param {string} columnId - Column ID
 * @returns {Promise} - Promise resolving to column options
 */
export const getMasterDataColumnOptionsAction = async (tableId, columnId) => {
    try {
        const response = await masterDataService.getColumnOptions(tableId, columnId);
        return response;
    } catch (error) {
        console.error('Error fetching column options:', error);
        throw error;
    }
};

/**
 * Get form field options (supports both static and master data)
 * @param {string} formId - Form ID
 * @param {string} fieldId - Field ID
 * @returns {Promise} - Promise resolving to field options
 */
export const getFormFieldOptionsAction = async (formId, fieldId) => {
    try {
        const response = await masterDataService.getFormFieldOptions(formId, fieldId);
        return response;
    } catch (error) {
        console.error('Error fetching form field options:', error);
        throw error;
    }
};

/**
 * Get table records with pagination
 * @param {string} tableId - Master data table ID
 * @param {Object} params - Query parameters (page, pageSize)
 * @returns {Promise} - Promise resolving to table records
 */
export const getMasterDataTableRecordsAction = async (tableId, params = {}) => {
    try {
        const response = await masterDataService.getTableRecords(tableId, params);
        return response;
    } catch (error) {
        console.error('Error fetching table records:', error);
        throw error;
    }
};

export const reAdmitDataAction = async (patientId, admissionData) => {
    try {
        return await patientService.addMedicalRecord(patientId, admissionData);
    } catch (error) {
        console.error('Error in reAdmitDataAction:', error);
        throw error;
    }
};

export const updateRecordStatus = async (recordId, statusData) => {
    try {
        // This would need to be implemented based on specific requirements
        return { success: true, message: "Record status updated successfully" };
    } catch (error) {
        console.error('Error in updateRecordStatus:', error);
        throw error;
    }
};

export const getDraftDataAction = async (params = {}) => {
    try {
        return await dynamicFormsService.getFormSubmissions(params.formId, { ...params, status: 'draft' });
    } catch (error) {
        console.error('Error in getDraftDataAction:', error);
        throw error;
    }
};

export const getFormDataByMasterAction = async (params = {}) => {
    try {
        return await dynamicFormsService.getFormSubmissions(params.formId, params);
    } catch (error) {
        console.error('Error in getFormDataByMasterAction:', error);
        throw error;
    }
};

// ============================================================================
// MEDICATION MANAGEMENT ACTIONS
// ============================================================================

export const searchMedicationsAction = async (params = {}) => {
    try {
        return await medicationService.searchMedications(params);
    } catch (error) {
        console.error('Error in searchMedicationsAction:', error);
        throw error;
    }
};

export const getMedicationByIdAction = async (medicationId) => {
    try {
        return await medicationService.getMedicationById(medicationId);
    } catch (error) {
        console.error('Error in getMedicationByIdAction:', error);
        throw error;
    }
};

export const createPrescriptionAction = async (prescriptionData) => {
    try {
        return await medicationService.createPrescription(prescriptionData);
    } catch (error) {
        console.error('Error in createPrescriptionAction:', error);
        throw error;
    }
};

export const getPrescriptionsAction = async (params = {}) => {
    try {
        return await medicationService.getPrescriptions(params);
    } catch (error) {
        console.error('Error in getPrescriptionsAction:', error);
        throw error;
    }
};

export const getMedpassPrescriptionListAction = async (data = {}) => {
    try {
        return await medicationService.getMedpassPrescriptionList(data);
    } catch (error) {
        console.error('Error in getMedpassPrescriptionListAction:', error);
        throw error;
    }
};

// ============================================================================
// SCHEDULING ACTIONS
// ============================================================================

// Employee Management Actions
export const getEmployeesAction = async (params = {}) => {
    try {
        return await schedulingService.getEmployees(params);
    } catch (error) {
        console.error('Error in getEmployeesAction:', error);
        throw error;
    }
};

export const getEmployeeByIdAction = async (employeeId) => {
    try {
        return await schedulingService.getEmployeeById(employeeId);
    } catch (error) {
        console.error('Error in getEmployeeByIdAction:', error);
        throw error;
    }
};

export const createEmployeeAction = async (employeeData) => {
    try {
        return await schedulingService.createEmployee(employeeData);
    } catch (error) {
        console.error('Error in createEmployeeAction:', error);
        throw error;
    }
};

export const updateEmployeeAction = async (employeeId, employeeData) => {
    try {
        return await schedulingService.updateEmployee(employeeId, employeeData);
    } catch (error) {
        console.error('Error in updateEmployeeAction:', error);
        throw error;
    }
};


export const createScheduleAction = async (scheduleData) => {
    try {
        return await schedulingService.createSchedule(scheduleData);
    } catch (error) {
        console.error('Error in createScheduleAction:', error);
        throw error;
    }
};

export const getSchedulesAction = async (params = {}) => {
    try {
        return await schedulingService.getSchedules(params);
    } catch (error) {
        console.error('Error in getSchedulesAction:', error);
        throw error;
    }
};

export const getStaffDutyAction = async (params = {}) => {
    try {
        return await schedulingService.getStaffDuty(params);
    } catch (error) {
        console.error('Error in getStaffDutyAction:', error);
        throw error;
    }
};

export const getAppointmentsAction = async (params = {}) => {
    try {
        return await schedulingService.getAppointments(params);
    } catch (error) {
        console.error('Error in getAppointmentsAction:', error);
        throw error;
    }
};

export const createAppointmentAction = async (appointmentData) => {
    try {
        return await schedulingService.createAppointment(appointmentData);
    } catch (error) {
        console.error('Error in createAppointmentAction:', error);
        throw error;
    }
};

// ============================================================================
// DOCUMENT MANAGEMENT ACTIONS
// ============================================================================

export const uploadDocumentAction = async (uploadData) => {
    try {
        return await documentService.uploadDocument(uploadData);
    } catch (error) {
        console.error('Error in uploadDocumentAction:', error);
        throw error;
    }
};

export const getPatientDocumentsAction = async (patientId, params = {}) => {
    try {
        return await documentService.getPatientDocuments(patientId, params);
    } catch (error) {
        console.error('Error in getPatientDocumentsAction:', error);
        throw error;
    }
};

export const downloadDocumentAction = async (documentId, justification = null) => {
    try {
        return await documentService.downloadDocument(documentId, justification);
    } catch (error) {
        console.error('Error in downloadDocumentAction:', error);
        throw error;
    }
};

// ============================================================================
// AUDIT & LOGGING ACTIONS
// ============================================================================

export const getAuditLogAction = async (params = {}) => {
    try {
        const result = await auditService.getAuditEntries(params);
        if (result && result.success) {
            return result.data;
        }
        return {
            entries: [],
            total: 0,
            page: 1,
            page_size: 20,
            total_pages: 0
        };
    } catch (error) {
        console.error('Error in getAuditLogAction:', error);
        return {
            entries: [],
            total: 0,
            page: 1,
            page_size: 20,
            total_pages: 0,
            message: error.message || "Failed to fetch audit logs"
        };
    }
};


// ============================================================================
// DASHBOARD ACTIONS
// ============================================================================

export const getDashboardStatsAction = async (params = {}) => {
    try {
        return await dashboardService.getDashboardStatistics(params);
    } catch (error) {
        console.error('Error in getDashboardStatsAction:', error);
        throw error;
    }
};

export const getLegacyDashboardStatsAction = async (requestData = {}) => {
    try {
        return await dashboardService.getLegacyDashboardStats(requestData);
    } catch (error) {
        console.error('Error in getLegacyDashboardStatsAction:', error);
        throw error;
    }
};

export const getSystemStatsAction = async (requestData = {}) => {
    try {
        return await dashboardService.getSystemStats(requestData);
    } catch (error) {
        console.error('Error in getSystemStatsAction:', error);
        throw error;
    }
};

export const getNotificationsAction = async (params = {}) => {
    try {
        return await dashboardService.getNotifications(params);
    } catch (error) {
        console.error('Error in getNotificationsAction:', error);
        throw error;
    }
};

export const getFormMenuAction = async (params = {}) => {
    try {
        return await dashboardService.getFormMenu(params);
    } catch (error) {
        console.error('Error in getFormMenuAction:', error);
        throw error;
    }
};

export const getRecentActivitiesAction = async (params = {}) => {
    try {
        return await dashboardService.getRecentActivities(params);
    } catch (error) {
        console.error('Error in getRecentActivitiesAction:', error);
        throw error;
    }
};

export const getUpcomingEventsAction = async (params = {}) => {
    try {
        return await dashboardService.getUpcomingEvents(params);
    } catch (error) {
        console.error('Error in getUpcomingEventsAction:', error);
        throw error;
    }
};

// Export all actions for easy importing
export * from './admin.action'; // Keep legacy actions for backward compatibility